<script lang="ts">
  // Card Shop overlay, opened by clicking the café storefront in Station Square.
  // Two tabs: BUY (today's rotating selection of GX-era boosters) and OPEN (the
  // packs you own, unopened). Opening a pack reveals its cards with a flip-in.
  // VRAINS data-window vocabulary, matching the in-world menu + NPC dialog.
  import { fade, scale, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { type ServerMessage, type ShopPack, type ShopEra, type Wallet, type GainedCard } from "@ygoevo/world-protocol";
  import { send, listen } from "../stores/net.svelte.ts";
  import DataWindow from "./ui/DataWindow.svelte";
  import Toast from "./ui/Toast.svelte";
  import PackReveal from "./PackReveal.svelte";
  import PackArt from "./PackArt.svelte";
  import { onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  type Catalog = Extract<ServerMessage, { t: "ShopCatalog" }>;

  let {
    shop,
    catalog,
    wallet,
    onClose,
  }: {
    /** Which shop the player walked into, the source of truth for the header, so the
     *  name/era is right immediately (and even if a stale server omits it from the catalog). */
    shop: ShopEra;
    catalog: Catalog | null;
    wallet: Wallet;
    onClose: () => void;
  } = $props();

  // Cosmetics (sleeves + duel disks) are no longer sold here, they're rolled from the Cosmetic
  // Boxes (the Shop button). This café is packs only.
  let tab = $state<"buy" | "open">("buy");

  let busy = $state(false); // a buy/open round-trip is in flight
  let pendingOpen = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "err"; text: string } | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | undefined;

  // Pack reveal (after opening): the pack + the passcodes it yielded (PackReveal
  // fetches card metadata + rarity itself for the flip animation).
  let reveal = $state<{ pack: ShopPack; gained: GainedCard[] } | null>(null);

  // Each physical shop sells a different anime era of the booster line. The header
  // names the shop + its era so the rotating selection reads as in-world, not random.
  const SHOP_META: Record<string, { name: string; era: string }> = {
    kame: { name: "Kame Game Shop", era: "Original Era · ’02–’04" },
    academy: { name: "Academy Card Shop", era: "GX Era · ’05–’08" },
  };
  const shopMeta = $derived(SHOP_META[shop] ?? SHOP_META.kame!);

  const byId = $derived(new Map((catalog?.library ?? []).map((p) => [p.id, p])));
  const price = $derived(catalog?.price ?? 100);
  const openSize = $derived(catalog?.openSize ?? 5);
  // Per-pack set-completion: distinct cards owned / pool size (the "% collected" badge).
  const collection = $derived(catalog?.collection ?? {});

  // Per-pack buy quantity (the stepper next to each Buy button). Defaults to 1.
  let qty = $state<Record<string, number>>({});
  const qtyOf = (id: string) => qty[id] ?? 1;
  const setQty = (id: string, n: number) => { qty[id] = Math.max(1, Math.min(99, n)); };
  const todayPacks = $derived(
    (catalog?.todayIds ?? []).map((id) => byId.get(id)).filter((p): p is ShopPack => !!p),
  );
  const ownedPacks = $derived(
    Object.entries(wallet.packs ?? {})
      .filter(([, n]) => n > 0)
      .map(([id, n]) => ({ pack: byId.get(id), n }))
      .filter((e): e is { pack: ShopPack; n: number } => !!e.pack),
  );
  const ownedCount = $derived(ownedPacks.reduce((s, e) => s + e.n, 0));

  function flash(kind: "ok" | "err", text: string) {
    toast = { kind, text };
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 2600);
  }

  function buy(p: ShopPack) {
    const n = qtyOf(p.id);
    if (busy || wallet.dp < price * n) return;
    busy = true;
    send({ t: "ShopBuy", itemId: p.id, qty: n });
  }

  function open(p: ShopPack) {
    if (busy) return;
    busy = true;
    pendingOpen = p.id;
    send({ t: "PackOpen", packId: p.id });
  }

  // Open every unopened pack: the server bounds each PackOpenAll batch, so the reply handler
  // keeps re-sending until the wallet is empty, then shows ONE combined reveal of everything.
  let bulkGained: GainedCard[] = [];
  let bulkDust = 0;
  function openAll() {
    if (busy || ownedCount === 0) return;
    busy = true;
    bulkGained = [];
    bulkDust = 0;
    pendingOpen = "*"; // sentinel: a bulk open, matched against the PackOpened reply below
    send({ t: "PackOpenAll" });
  }

  listen("ShopBought", (m) => {
    busy = false;
    const p = byId.get(m.itemId);
    flash("ok", `Bought ${m.qty > 1 ? `${m.qty}× ` : ""}${p?.name ?? m.itemId}`);
  });

  listen("PackOpened", (m) => {
    if (m.packId !== pendingOpen) return; // not the open we initiated here
    const bulk = pendingOpen === "*";
    if (bulk) {
      bulkGained.push(...m.gained);
      bulkDust += m.dustedShards ?? 0;
      // Packs left after this batch (the server caps each request) → keep opening.
      if (Object.values(m.wallet.packs ?? {}).some((n) => (n ?? 0) > 0)) {
        send({ t: "PackOpenAll" });
        return;
      }
    }
    pendingOpen = null;
    // A bulk open has no single pack, give the reveal a synthetic header.
    const pack = bulk ? ({ id: "*", name: "All Packs", image: "", releaseDate: "" } as ShopPack) : byId.get(m.packId);
    if (pack) reveal = { pack, gained: bulk ? bulkGained : m.gained };
    busy = false;
    // 4th+ copies you already maxed out are auto-dismantled into shards.
    const dust = bulk ? bulkDust : (m.dustedShards ?? 0);
    if (dust) flash("ok", `+${dust} shards from duplicates`);
  });

  listen("Error", (m) => {
    if (!busy && !pendingOpen) return;
    busy = false;
    pendingOpen = null;
    flash("err", m.message || "Something went wrong");
  });

  // Escape backs out of a pack reveal first, then closes the shop. `ignoreInputs`
  // keeps it from closing the shop while the player is typing in the card search.
  onEscape(() => { if (reveal) reveal = null; else onClose(); }, () => ({ ignoreInputs: true }));
</script>

<div class="mobile-dialog-host fixed inset-0 z-40 grid place-items-center" role="presentation">
  <button
    class="modal-scrim absolute inset-0 bg-void/50"
    transition:fade={{ duration: 160 }}
    onclick={onClose}
    aria-label="Leave the shop"
  ></button>

  <div
    class="mobile-full-dialog relative w-[min(960px,95vw)]"
    role="dialog"
    aria-modal="true"
    aria-label={shopMeta.name}
    use:draggableDialog
    transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}
  >
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <!-- header -->
      <div data-dialog-drag-handle class="dialog-drag-handle flex items-center justify-between gap-3 border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-3">
        <span class="flex items-center gap-2.5">
          <span class="text-xl">🛒</span>
          <span class="flex flex-col leading-tight">
            <span class="font-display text-base font-bold uppercase tracking-[0.18em] text-cyan text-glow">
              {shopMeta.name}
            </span>
            <span class="font-display text-[10px] uppercase tracking-[0.2em] text-ink-dim">
              {shopMeta.era}
            </span>
          </span>
        </span>
        <div class="flex items-center gap-3">
          <span class="flex items-baseline gap-1.5 chamfer-btn bg-void-2/60 px-3 py-1.5" style="--c:7px">
            <span class="text-[10px] uppercase tracking-widest text-ink-dim">DP</span>
            <span class="font-display text-base font-bold text-gold tabular-nums">{wallet.dp.toLocaleString()}</span>
          </span>
          <button
            onclick={onClose}
            class="grid size-8 place-items-center text-ink-dim transition-colors hover:text-cyan"
            aria-label="Close shop"
          >
            <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      </div>

      <!-- tabs -->
      <div class="flex items-center gap-1 border-b border-cyan-dim/15 px-3 pt-2.5">
        <button
          onclick={() => (tab = "buy")}
          class="chamfer-btn px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] transition-colors {tab === 'buy' ? 'bg-cyan/15 text-cyan glow-cyan' : 'bg-transparent text-ink-dim hover:text-ink'}"
          style="--c:6px"
        >
          Buy Packs
        </button>
        <button
          onclick={() => (tab = "open")}
          class="chamfer-btn px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] transition-colors {tab === 'open' ? 'bg-cyan/15 text-cyan glow-cyan' : 'bg-transparent text-ink-dim hover:text-ink'}"
          style="--c:6px"
        >
          My Packs{#if ownedCount}<span class="ml-1.5 rounded-full bg-cyan/20 px-1.5 text-[0.6rem] text-cyan">{ownedCount}</span>{/if}
        </button>
        <span class="ml-auto pr-2 pb-1 text-[0.66rem] uppercase tracking-[0.14em] text-ink-dim">
          {tab === "buy" ? "Selection refreshes daily" : `Each pack = ${openSize} cards`}
        </span>
      </div>

      <!-- body, FIXED height so the window never resizes between tabs; content
           scrolls within. Sized to show two rows of the (capped) cards without a
           scrollbar; longer lists (My Packs) scroll. -->
      <div class="mobile-dialog-scroll h-[clamp(340px,64dvh,660px)] overflow-y-auto scroll-cyber p-4">
        {#if !catalog}
          <div class="grid place-items-center py-16 text-sm text-ink-dim">Loading the shelves…</div>
        {:else if tab === "buy"}
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {#each todayPacks as p (p.id)}
              {@const n = qtyOf(p.id)}
              {@const total = price * n}
              {@const afford = wallet.dp >= total}
              <div class="flex flex-col chamfer bg-void-2/40 p-2" style="--c:8px">
                {@render packArt(p)}
                <!-- quantity stepper: how many to buy at once -->
                <div class="mt-2 flex items-center justify-center gap-1.5">
                  <button onclick={() => setQty(p.id, n - 1)} disabled={busy || n <= 1} class="grid size-6 place-items-center chamfer-btn bg-void-2/60 font-display text-base font-bold leading-none text-cyan transition-colors hover:bg-cyan/15 disabled:opacity-30" style="--c:4px" aria-label="Buy one fewer">−</button>
                  <span class="min-w-[1.75rem] text-center font-display text-sm font-bold tabular-nums text-ink">{n}</span>
                  <button onclick={() => setQty(p.id, n + 1)} disabled={busy || n >= 99} class="grid size-6 place-items-center chamfer-btn bg-void-2/60 font-display text-base font-bold leading-none text-cyan transition-colors hover:bg-cyan/15 disabled:opacity-30" style="--c:4px" aria-label="Buy one more">+</button>
                </div>
                <button
                  onclick={() => buy(p)}
                  disabled={busy || !afford}
                  title={afford ? undefined : `Need ${(total - wallet.dp).toLocaleString()} more DP`}
                  class="mt-1.5 chamfer-btn py-1.5 font-display text-xs font-bold uppercase tracking-[0.1em] transition-colors {afford ? 'bg-gold/15 text-gold hover:bg-gold/25 hover:glow-gold' : 'bg-void-2/55 text-magenta/70'} disabled:cursor-not-allowed"
                  style="--c:6px"
                >
                  {total.toLocaleString()} DP
                </button>
              </div>
            {/each}
          </div>
        {:else if ownedPacks.length === 0}
          <div class="grid place-items-center gap-2 py-16 text-center">
            <span class="text-3xl opacity-60">📦</span>
            <span class="text-sm text-ink-dim">No unopened packs. Buy some from the shop!</span>
            <button onclick={() => (tab = "buy")} class="mt-1 chamfer-btn bg-cyan/15 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.12em] text-cyan hover:glow-cyan" style="--c:6px">Browse Packs</button>
          </div>
        {:else}
          <div class="mb-3 flex items-center justify-between gap-2">
            <span class="font-display text-[0.7rem] uppercase tracking-[0.14em] text-ink-dim">{ownedCount} unopened {ownedCount === 1 ? "pack" : "packs"}</span>
            <button
              onclick={openAll}
              disabled={busy}
              class="chamfer-btn bg-magenta/15 px-4 py-1.5 font-display text-xs font-bold uppercase tracking-[0.12em] text-magenta transition-colors hover:bg-magenta/25 hover:glow-magenta disabled:cursor-not-allowed disabled:opacity-50"
              style="--c:6px"
            >
              {busy && pendingOpen === "*" ? "Opening…" : "Open All"}
            </button>
          </div>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {#each ownedPacks as { pack, n } (pack.id)}
              <div class="relative flex flex-col chamfer bg-void-2/40 p-2" style="--c:8px">
                <span class="absolute right-2.5 top-2.5 z-10 chamfer-btn bg-void/85 px-1.5 py-0.5 font-display text-[0.62rem] font-bold text-cyan" style="--c:3px">×{n}</span>
                {@render packArt(pack)}
                <button
                  onclick={() => open(pack)}
                  disabled={busy}
                  class="mt-2 chamfer-btn bg-magenta/15 py-1.5 font-display text-xs font-bold uppercase tracking-[0.1em] text-magenta transition-colors hover:bg-magenta/25 hover:glow-magenta disabled:cursor-not-allowed disabled:opacity-50"
                  style="--c:6px"
                >
                  {busy && pendingOpen === pack.id ? "Opening…" : "Open"}
                </button>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </DataWindow>
  </div>

  <!-- toast -->
  {#if toast}
    <div
      class="pointer-events-none absolute bottom-6 left-1/2 z-50 -translate-x-1/2"
      transition:fly={{ y: 16, duration: 200 }}
    >
      <Toast kind={toast.kind === "ok" ? "success" : "error"}>{toast.text}</Toast>
    </div>
  {/if}
</div>

<!-- ── pack-opening reveal (animated flip + rarity glow) ─────────────────────── -->
{#if reveal}
  <PackReveal pack={reveal.pack} gained={reveal.gained} aggregate={reveal.pack.id === "*"} onClose={() => (reveal = null)} />
{/if}

{#snippet packArt(p: ShopPack)}
  {@const c = collection[p.id]}
  <div class="relative self-center aspect-[3/4] h-[clamp(150px,20dvh,210px)]">
    <PackArt name={p.name} art={p.art} kind="pack" />
    {#if c && c.total}
      <!-- set-completion: distinct cards collected from this pack's pool (gold once complete) -->
      <span
        class="absolute left-1.5 top-1.5 chamfer-btn bg-void/85 px-1.5 py-0.5 font-display text-[0.62rem] font-bold tabular-nums {c.have >= c.total ? 'text-gold' : 'text-cyan'}"
        style="--c:3px"
        title="{c.have}/{c.total} cards collected from this set"
      >{Math.round((c.have / c.total) * 100)}%</span>
    {/if}
  </div>
{/snippet}
